import { useState } from 'react';
import { auth, db } from '../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import Navbar from '../components/Navbar';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';

interface UserStats {
  tokens: number;
  gamesPlayed: number;
  highScores: {
    [key: string]: number;
  };
  displayName?: string;
}

const Profile = () => {
  const { userName, stats, refreshUserData } = useUser();
  const [isEditingName, setIsEditingName] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const validateDisplayName = (name: string) => {
    if (name.length < 3) {
      return 'Display name must be at least 3 characters';
    }
    if (name.length > 8) {
      return 'Display name cannot exceed 8 characters';
    }
    if (!/^[a-zA-Z0-9_\s]+$/.test(name)) {
      return 'Only letters, numbers, spaces and underscores allowed';
    }
    return '';
  };

  const handleUpdateName = async () => {
    if (!auth.currentUser) return;
    
    const validationError = validateDisplayName(displayName);
    if (validationError) {
      setError(validationError);
      return;
    }
    
    try {
      setError('');
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        displayName: displayName.trim()
      });
      await refreshUserData();
      setIsEditingName(false);
    } catch (error) {
      console.error('Error updating name:', error);
      setError('Failed to update display name');
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <Navbar 
        userName={userName} 
        displayName={stats?.displayName}
      />
      <div className="min-h-screen bg-slate-900 text-white p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-4xl font-bold">Profile</h1>
            {isEditingName ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => {
                      setDisplayName(e.target.value);
                      setError('');  // Clear error on input change
                    }}
                    className="bg-slate-700 text-white px-3 py-2 rounded-lg"
                    placeholder="Enter display name"
                    maxLength={8}
                  />
                  <button
                    onClick={handleUpdateName}
                    className="bg-green-500 px-4 py-2 rounded-lg hover:bg-green-600"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingName(false);
                      setError('');  // Clear error on cancel
                    }}
                    className="bg-red-500 px-4 py-2 rounded-lg hover:bg-red-600"
                  >
                    Cancel
                  </button>
                </div>
                {error && (
                  <span className="text-red-400 text-sm">{error}</span>
                )}
              </div>
            ) : (
              <button
                onClick={() => {
                  setDisplayName(stats?.displayName || '');
                  setIsEditingName(true);
                }}
                className="bg-indigo-500 px-4 py-2 rounded-lg hover:bg-indigo-600"
              >
                Edit Name
              </button>
            )}
          </div>
          
          <div className="bg-slate-800 rounded-lg p-6 shadow-xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-slate-700 rounded-lg">
                <h3 className="text-lg text-gray-400">Tokens</h3>
                <p className="text-3xl font-bold text-indigo-400">{stats?.tokens}</p>
              </div>
              <div className="text-center p-4 bg-slate-700 rounded-lg">
                <h3 className="text-lg text-gray-400">Games Played</h3>
                <p className="text-3xl font-bold text-indigo-400">{stats?.gamesPlayed}</p>
              </div>
              <div className="text-center p-4 bg-slate-700 rounded-lg">
                <h3 className="text-lg text-gray-400">Total Score</h3>
                <p className="text-3xl font-bold text-indigo-400">
                  {Object.values(stats?.highScores || {}).reduce((a, b) => a + b, 0)}
                </p>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="text-2xl font-bold mb-4">High Scores</h2>
              <div className="space-y-4">
                {Object.entries(stats?.highScores || {}).map(([game, score]) => (
                  <div key={game} className="flex justify-between items-center bg-slate-700 p-4 rounded-lg">
                    <span className="text-lg">{game}</span>
                    <span className="text-2xl font-bold text-indigo-400">{score}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile; 